/**
 * User Role Invitation Service - Phase 4 Architecture
 *
 * Handles role invitations independently of user management.
 * Extracted from UserService to reduce single-responsibility violations.
 */

import type { Database } from "@/integrations/supabase/database.types";
import { typedSupabase } from "@/lib/typed-supabase";
import {
  IService,
  ServiceResponse,
  createServiceError,
  createSuccessResponse,
  createErrorResponse,
  ServiceRegistry,
} from "@/services/core/service-registry";
import { AuditService } from "./auditService";
import { RoleInvitation } from "./types";
import { logger } from "@/lib/logger";

export interface IUserRoleInvitationService extends IService {
  sendRoleInvitation(params: {
    email: string;
    userId?: string;
    role: string;
    message?: string;
  }): Promise<ServiceResponse<{ invitationId: string; invitationToken: string }>>;
  cancelRoleInvitation(invitationId: string): Promise<ServiceResponse<void>>;
  getRoleInvitations(filters?: {
    status?: string;
    email?: string;
  }): Promise<ServiceResponse<RoleInvitation[]>>;
}

export class UserRoleInvitationService implements IUserRoleInvitationService {
  readonly name = "user-role-invitation" as const;
  private readonly supabase = typedSupabase;
  private auditService?: AuditService;
  private registry: ServiceRegistry;

  constructor(serviceRegistry: ServiceRegistry) {
    this.registry = serviceRegistry;
  }

  async initialize(): Promise<void> {
    try {
      this.auditService = await this.registry.get<AuditService>("audit");
      logger.info("✉️ UserRoleInvitationService initialized (audit ready)");
    } catch (e) {
      logger.warn("⚠️ UserRoleInvitationService initialized without audit; will retry on first use", { error: e });
    }
  }

  async dispose(): Promise<void> {
    logger.info("✉️ UserRoleInvitationService disposed");
  }

  private async ensureAudit(): Promise<AuditService> {
    this.auditService ??= await this.registry.get<AuditService>("audit");
    return this.auditService;
  }

  async sendRoleInvitation(params: {
    email: string;
    userId?: string;
    role: string;
    message?: string;
  }): Promise<ServiceResponse<{ invitationId: string; invitationToken: string }>> {
    try {
      const { data: { user: currentUser } } = await this.supabase.auth.getUser();
      if (!currentUser) {
        return createErrorResponse(
          createServiceError("AUTH_ERROR", "User not authenticated", this.name)
        );
      }

      const { data: inviterProfile } = await this.supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", currentUser.id)
        .single();

      const inviterName = (inviterProfile?.full_name ?? inviterProfile?.email) ?? "Admin";

      const { data: invitation, error: createError } = await this.supabase
        .from("role_invitations")
        .insert({
          email: params.email,
          user_id: params.userId ?? null,
          role: params.role as RoleInvitation['role'],
          invited_by: currentUser.id,
          message: params.message,
        })
        .select("id, invitation_token, expires_at")
        .single();

      if (createError || !invitation) {
        logger.error("Failed to create invitation:", { error: createError });
        return createErrorResponse(
          createServiceError("DATABASE_ERROR", "Failed to create invitation", this.name)
        );
      }

      const { emailService } = await import("@/services/integrations/email.service");

      const emailResult = await emailService.sendRoleInvitationEmail({
        recipientEmail: params.email,
        recipientName: params.email.split("@")[0],
        role: params.role,
        invitationToken: invitation.invitation_token,
        inviterName,
        message: params.message,
        expiresAt: invitation.expires_at,
      });

      if (!emailResult.success) {
        logger.error("Failed to send invitation email:", { error: emailResult.error });
      }

      await (await this.ensureAudit()).logAdminAction({
        action: "send_role_invitation",
        entity_type: "user_role",
        entity_id: params.userId ?? params.email,
        details: {
          email: params.email,
          role: params.role,
          invitation_id: invitation.id,
        },
      });

      return createSuccessResponse({
        invitationId: invitation.id,
        invitationToken: invitation.invitation_token,
      });
    } catch (error) {
      logger.error("Error sending role invitation:", { error: error });
      return createErrorResponse(
        createServiceError(
          "SERVICE_ERROR",
          error instanceof Error ? error.message : "Failed to send role invitation",
          this.name
        )
      );
    }
  }

  async cancelRoleInvitation(invitationId: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from("role_invitations")
        .update({
          status: "cancelled",
        })
        .eq("id", invitationId)
        .eq("status", "pending");

      if (error) {
        logger.error("Failed to cancel invitation:", { error: error });
        return createErrorResponse(
          createServiceError("DATABASE_ERROR", "Failed to cancel invitation", this.name)
        );
      }

      await (await this.ensureAudit()).logAdminAction({
        action: "cancel_role_invitation",
        entity_type: "user_role",
        entity_id: invitationId,
        details: { invitation_id: invitationId },
      });

      return createSuccessResponse(undefined);
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          "SERVICE_ERROR",
          error instanceof Error ? error.message : "Failed to cancel invitation",
          this.name
        )
      );
    }
  }

  async getRoleInvitations(filters?: {
    status?: string;
    email?: string;
  }): Promise<ServiceResponse<RoleInvitation[]>> {
    try {
      let query = this.supabase
        .from("role_invitations")
        .select(`
          id,
          email,
          user_id,
          role,
          status,
          message,
          expires_at,
          sent_at,
          accepted_at,
          created_at,
          profiles!role_invitations_user_id_fkey (
            full_name,
            email
          ),
          invited_by_profile:profiles!role_invitations_invited_by_fkey (
            full_name,
            email
          )
        `)
        .order("created_at", { ascending: false });

      if (filters?.status) {
        query = query.eq("status", filters.status);
      }

      if (filters?.email) {
        query = query.ilike("email", `%${filters.email}%`);
      }

      const { data, error } = await query;

      if (error) {
        logger.error("Failed to fetch invitations:", { error: error });
        return createErrorResponse(
          createServiceError("DATABASE_ERROR", "Failed to fetch invitations", this.name)
        );
      }

      const formattedData: RoleInvitation[] = (data || []).map((item) => {
        const i = item as unknown as {
          id: string;
          email: string;
          user_id: string | null;
          role: RoleInvitation['role'];
          status: string;
          message: string | null;
          expires_at: string;
          sent_at: string | null;
          accepted_at: string | null;
          created_at: string | null;
          profiles: { full_name: string | null; email: string | null } | null;
          invited_by_profile: { full_name: string | null; email: string | null } | null;
        };
        return {
          id: i.id,
          email: i.email,
          user_id: i.user_id,
          role: i.role,
          status: i.status,
          message: i.message,
          expires_at: i.expires_at,
          sent_at: i.sent_at,
          accepted_at: i.accepted_at,
          created_at: i.created_at,
          profiles: i.profiles,
          invited_by_profile: i.invited_by_profile
        };
      });

      return createSuccessResponse(formattedData);
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          "SERVICE_ERROR",
          error instanceof Error ? error.message : "Failed to fetch invitations",
          this.name
        )
      );
    }
  }
}
